// src/engines/EngineBanner.jsx
// ══════════════════════════════════════════════════════════════════════════════
// ENGINE BANNER — Dos modos:
//
//   <EngineBanner mode="block" ... />  → Banner tras completar un bloque (X/10)
//   <EngineBanner mode="class" ... />  → Banner final: tabla completa + grade
//
// Props bloque:
//   blockLabel   string      — Nombre del bloque
//   blockNum     number      — Índice del bloque (0-indexed)
//   totalBlocks  number      — Total de bloques del motor
//   score        number      — Puntos obtenidos (0-10)
//   maxScore     number      — Siempre 10
//   errors       number      — Ejercicios incorrectos
//   timeSeconds  number      — Tiempo empleado
//   isLastBlock  bool        — Si es el último bloque
//   lessonTitle  string      — Título de la lección
//   onContinue   fn()        — Ir al siguiente bloque
//   onRepeat     fn()        — Repetir el bloque actual
//   onExit       fn()        — Salir a la lección
//
// Props clase (mode="class"):
//   lessonTitle  string
//   motorType    string
//   blockScores  []{blockLabel, score, maxScore, completed, block_num}
//   summary      {average, grade, gradeLabel, totalScore, maxPossible, blocksDone}
//   onRepeat     fn()
//   onExit       fn()
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from "react";
import "./EngineBanner.css";

// ─── UTILS ────────────────────────────────────────────────────────────────────

const pct = (score, max) =>
    max > 0 ? Math.round((score / max) * 100) : 0;

const gradeEmoji = (g) =>
    ({ S: "🏆", A: "⚡", B: "🚀", C: "💪", D: "🔄" }[g] || "📚");

const gradeColor = (g) =>
({
    S: "#FFED4E",
    A: "#3DEBA0",
    B: "#5C9EFF",
    C: "#FF8C42",
    D: "#FF5C6A",
}[g] || "#F0F0F8");

const gradeLabel = (g) =>
({
    S: "Maestría Absoluta",
    A: "Nivel Avanzado",
    B: "Buen Camino",
    C: "Completado",
    D: "Repasa y Continúa",
}[g] || "Completado");

function computeGrade(avg) {
    if (avg >= 90) return "S";
    if (avg >= 80) return "A";
    if (avg >= 70) return "B";
    if (avg >= 60) return "C";
    return "D";
}

function formatTime(secs) {
    if (!secs) return "—";
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

// ─── SCORE RING ───────────────────────────────────────────────────────────────
function ScoreRing({ score, maxScore = 10, size = 96 }) {
    const p = pct(score, maxScore);
    const r = (size - 12) / 2;
    const circ = 2 * Math.PI * r;
    const dash = circ * (p / 100);
    const grade = computeGrade(p);
    const color = gradeColor(grade);

    return (
        <div className="ebn-ring-wrap" style={{ position: "relative", flexShrink: 0, width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={10}
                />
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none"
                    stroke={color}
                    strokeWidth={10}
                    strokeDasharray={`${dash} ${circ}`}
                    strokeLinecap="round"
                    style={{
                        filter: `drop-shadow(0 0 6px ${color}88)`,
                        transition: "stroke-dasharray 1s cubic-bezier(0.34,1.56,0.64,1)",
                    }}
                />
            </svg>
            <div className="ebn-ring-center">
                <span className="ebn-ring-score" style={{ color }}>{score}/{maxScore}</span>
                <span className="ebn-ring-grade" style={{ color }}>{grade}</span>
            </div>
        </div>
    );
}

// ─── CONFETTI ─────────────────────────────────────────────────────────────────
// Se exporta porque lo usa tambien la pantalla de bloque completado del modo IA
// (`BlockResult` en EngineMode). El modo clasico tiene el suyo —`launchConfetti`
// en utils/effects.js— y NO se reutiliza a proposito: su paleta es morada y rosa
// de otra epoca del producto, y en una pantalla de FLUENCY NEXUS se ve prestada.
export function Confetti({ active }) {
    // Las posiciones se calculan UNA vez. Estaban en el cuerpo del render, y
    // cualquier re-render del padre volvia a sortearlas: las 48 piezas saltaban
    // de sitio a mitad de caida. Con `useMemo` caen donde nacieron.
    const piezas = useMemo(() => {
        const cols = ["#FFED4E", "#3DEBA0", "#5C9EFF", "#FF5C6A", "#FF8C42", "#fff", "#a78bfa"];
        return Array.from({ length: 48 }, (_, i) => ({
            left: `${Math.random() * 100}%`,
            background: cols[i % cols.length],
            borderRadius: i % 2 === 0 ? "50%" : "2px",
            animationDelay: `${Math.random() * 1}s`,
        }));
    }, []);

    if (!active) return null;
    return (
        <>
            {piezas.map((estilo, i) => (
                <div key={i} className="ebn-confetti-piece" style={estilo} />
            ))}
        </>
    );
}

// ─── BLOCK BANNER ─────────────────────────────────────────────────────────────
function BlockBanner({
    blockLabel,
    blockNum,
    totalBlocks,
    score,
    maxScore = 10,
    errors = 0,
    timeSeconds,
    isLastBlock,
    lessonTitle,
    onContinue,
    onRepeat,
    onExit,
}) {
    const p = pct(score, maxScore);
    const grade = computeGrade(p);
    const color = gradeColor(grade);
    const perfect = score === maxScore;
    const [show, setShow] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setShow(true), 80);
        return () => clearTimeout(t);
    }, []);

    return (
        <div className={`ebn-wrap ${show ? "ebn-visible" : ""}`}>
            <Confetti active={perfect} />

            {/* ── Top bar ─── */}
            <div className="ebn-top-bar">
                <span className="ebn-lesson-name">{lessonTitle}</span>
                <span className="ebn-block-pill">
                    Bloque {blockNum + 1} / {totalBlocks}
                </span>
            </div>

            {/* ── Score ring + label ─── */}
            <div className="ebn-ring-section">
                <ScoreRing score={score} maxScore={maxScore} size={110} />
                <div className="ebn-ring-info">
                    <div className="ebn-ring-block-name">{blockLabel}</div>
                    <div className="ebn-ring-grade-label" style={{ color }}>
                        {gradeEmoji(grade)} {gradeLabel(grade)}
                    </div>
                </div>
            </div>

            {/* ── Stats row ─── */}
            <div className="ebn-stats">
                {[
                    ["Puntos", `${score}/${maxScore}`, score === maxScore ? "#3DEBA0" : color],
                    ["Errores", errors === 0 ? "¡Cero! 🔥" : `${errors}`, errors === 0 ? "#3DEBA0" : "#FF5C6A"],
                    ["Tiempo", formatTime(timeSeconds), "#F0F0F8"],
                ].map(([label, val, valColor]) => (
                    <div key={label} className="ebn-stat">
                        <div className="ebn-stat-val" style={{ color: valColor }}>{val}</div>
                        <div className="ebn-stat-lbl">{label}</div>
                    </div>
                ))}
            </div>

            {/* ── Progress bar visual ─── */}
            <div className="ebn-prog-bar-wrap">
                <div className="ebn-prog-segs">
                    {Array.from({ length: totalBlocks }, (_, i) => (
                        <div
                            key={i}
                            className={`ebn-prog-seg ${i === blockNum ? "ebn-seg-active" : ""}`}
                            style={{
                                flex: 1,
                                background:
                                    i < blockNum
                                        ? "#3DEBA0"
                                        : i === blockNum
                                            ? color
                                            : "rgba(255,255,255,0.08)",
                            }}
                        />
                    ))}
                </div>
                <div className="ebn-prog-label">
                    {blockNum + 1} de {totalBlocks} bloques completados
                </div>
            </div>

            {/* ── CTAs ─── */}
            <div className="ebn-ctas">
                {!isLastBlock ? (
                    <>
                        <button className="ebn-btn-primary" onClick={onContinue}>
                            {gradeEmoji(grade)} Siguiente Bloque →
                        </button>
                        {score < maxScore && (
                            <button className="ebn-btn-secondary" onClick={onRepeat}>
                                🔄 Repetir Bloque
                            </button>
                        )}
                    </>
                ) : (
                    <button className="ebn-btn-primary" onClick={onContinue}>
                        🎓 Ver Resumen Final →
                    </button>
                )}
                <button className="ebn-btn-ghost" onClick={onExit}>
                    ← Salir de la clase
                </button>
            </div>
        </div>
    );
}

// ─── CLASS BANNER (FINAL SUMMARY) ─────────────────────────────────────────────
function ClassBanner({
    lessonTitle,
    motorType,
    blockScores = [],
    summary = {},
    onRepeat,
    onExit,
}) {
    const {
        average = 0,
        grade: g,
        gradeLabel: gLabel,
        totalScore = 0,
        maxPossible = 0,
        blocksDone = 0,
    } = summary;

    const grade = g || computeGrade(average);
    const label = gLabel || gradeLabel(grade);
    const color = gradeColor(grade);
    const [show, setShow] = useState(false);
    const perfect = average >= 90;

    useEffect(() => {
        const t = setTimeout(() => setShow(true), 100);
        return () => clearTimeout(t);
    }, []);

    const motorLabel = {
        verb_tense: "Motor 1 · Verb Tenses",
        grammar_structure: "Motor 2 · Grammar Structures",
        parts_of_speech: "Motor 3 · Parts of Speech",
        modal_verbs: "Motor 4 · Modal Verbs",
        phrasal_verbs: "Motor 5 · Phrasal Verbs",
        vocabulary_thematic: "Motor 6 · Vocabulary",
        functional_language: "Motor 8 · Functional Language",
        academic_writing: "Motor 9 · Academic Writing",
    }[motorType] || motorType;

    return (
        <div className={`ebn-wrap ${show ? "ebn-visible" : ""} ebn-class-section`}>
            <Confetti active={perfect} />

            {/* ── Header ─── */}
            <div className="ebn-class-header">
                <div className="ebn-trophy">{gradeEmoji(grade)}</div>
                <div>
                    <div className="ebn-class-title">¡Clase Completada!</div>
                    <div className="ebn-class-lesson">{lessonTitle}</div>
                    <div className="ebn-class-motor">{motorLabel}</div>
                </div>
            </div>

            {/* ── Big grade ─── */}
            <div className="ebn-class-grade-section">
                <ScoreRing score={totalScore} maxScore={maxPossible} size={120} />
                <div className="ebn-class-grade-info">
                    <div className="ebn-class-grade-val" style={{ color }}>
                        Grade {grade}
                    </div>
                    <div className="ebn-class-grade-lbl" style={{ color }}>
                        {label}
                    </div>
                    <div className="ebn-class-avg">{Math.round(average)}% promedio</div>
                </div>
            </div>

            {/* ── Block by block table ─── */}
            <div className="ebn-table">
                <div className="ebn-table-header">
                    <span>Bloque</span>
                    <span>Puntos</span>
                    <span>%</span>
                </div>
                {blockScores.map((b, i) => {
                    const bp = b.completed ? pct(b.score, b.maxScore || 10) : null;
                    const bg = bp !== null ? computeGrade(bp) : null;
                    const bc = bg ? gradeColor(bg) : "rgba(255,255,255,0.18)";
                    return (
                        <div key={i} className="ebn-table-row">
                            <span className="ebn-table-name">
                                <span className="ebn-table-dot" style={{ background: bc }} />
                                {b.blockLabel || b.block_name || `Bloque ${i + 1}`}
                            </span>
                            <span style={{ color: bc, fontWeight: 700, fontSize: 13 }}>
                                {b.completed ? `${b.score}/${b.maxScore || 10}` : "—"}
                            </span>
                            <span style={{ color: bc, fontWeight: 700, fontSize: 12 }}>
                                {b.completed ? `${bp}%` : "—"}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* ── Summary stats ─── */}
            <div className="ebn-summary-stats">
                {[
                    ["Bloques", `${blocksDone}/${blockScores.length}`],
                    ["Total", `${totalScore}/${maxPossible} pts`],
                    ["Promedio", `${Math.round(average)}%`],
                ].map(([lbl, val]) => (
                    <div key={lbl} className="ebn-summary-stat">
                        <div className="ebn-summary-stat-val">{val}</div>
                        <div className="ebn-summary-stat-lbl">{lbl}</div>
                    </div>
                ))}
            </div>

            {/* ── CTAs ─── */}
            <div className="ebn-ctas">
                <button className="ebn-btn-secondary" onClick={onRepeat}>
                    🔄 Repetir Clase Completa
                </button>
                <button className="ebn-btn-primary" onClick={onExit}>
                    ✅ Listo — Ir al Dashboard
                </button>
            </div>
        </div>
    );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function EngineBanner({ mode = "block", ...props }) {
    if (mode === "class") return <ClassBanner {...props} />;
    return <BlockBanner {...props} />;
}